import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { FileText, ListChecks, GitCompare, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileListRow } from "@/components/app/mobile/mobile-list-row";

export const Route = createFileRoute("/_authenticated/app/documents")({
  component: DocumentsHub,
});

function DocumentsHub() {
  const { t } = useTranslation();
  const loc = useLocation();
  const isMobile = useIsMobile();
  const value = loc.pathname.includes("compare")
    ? "compare"
    : loc.pathname.includes("analyses")
    ? "clauses"
    : "summary";

  if (isMobile) {
    return (
      <div className="mob-surface-0 min-h-dvh px-4 pt-4 text-foreground">
        <div className="overflow-hidden rounded-2xl border mob-hairline mob-surface-1 divide-y divide-white/5">
          <Link to="/app/analysis"><MobileListRow leading={<FileText className="h-[18px] w-[18px]" />} title={t("pages.documents.summaryTitle")} subtitle={t("pages.documents.summaryDesc")} /></Link>
          <Link to="/app/analyses"><MobileListRow leading={<ListChecks className="h-[18px] w-[18px]" />} title={t("pages.documents.clausesTitle")} subtitle={t("pages.documents.clausesDesc")} /></Link>
          <MobileListRow leading={<GitCompare className="h-[18px] w-[18px]" />} title={t("pages.documents.compareTitle")} subtitle={t("pages.documents.comingSoon")} chevron={false} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t("pages.documents.title")}
        description={t("pages.documents.description")}
      />
      <PageBody>
        <Tabs value={value} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="summary" asChild>
              <Link to="/app/documents" className="gap-2">
                <FileText className="h-4 w-4" /> {t("pages.documents.tabSummary")}
              </Link>
            </TabsTrigger>
            <TabsTrigger value="clauses" asChild>
              <Link to="/app/analyses" className="gap-2">
                <ListChecks className="h-4 w-4" /> {t("pages.documents.tabClauses")}
              </Link>
            </TabsTrigger>
            <TabsTrigger value="compare" disabled className="gap-2">
              <GitCompare className="h-4 w-4" /> {t("pages.documents.tabCompare")}
              <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("pages.documents.beta")}
              </span>
            </TabsTrigger>
          </TabsList>

          <div className="grid gap-4 md:grid-cols-3">
            <Card className="p-5">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-semibold">{t("pages.documents.summaryTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("pages.documents.summaryDesc")}
              </p>
              <Button asChild size="sm" className="mt-4">
                <Link to="/app/analysis" className="gap-1.5">
                  {t("pages.documents.openAnalysis")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </Card>

            <Card className="p-5">
              <ListChecks className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-semibold">{t("pages.documents.clausesTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("pages.documents.clausesDesc")}
              </p>
              <Button asChild size="sm" variant="outline" className="mt-4">
                <Link to="/app/analyses" className="gap-1.5">
                  {t("pages.documents.seeHistory")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </Card>

            <Card className="p-5 opacity-70">
              <GitCompare className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-semibold">{t("pages.documents.compareTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("pages.documents.compareDesc")}
              </p>
              <Button size="sm" variant="outline" className="mt-4" disabled>
                {t("pages.documents.comingSoon")}
              </Button>
            </Card>
          </div>
        </Tabs>
      </PageBody>
    </div>
  );
}
